import { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

function Lightbox({ images, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const img = images[idx];

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx(i => Math.min(i + 1, images.length - 1));
      if (e.key === 'ArrowLeft') setIdx(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [images.length, onClose]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white z-10"><X size={24} /></button>
      <div className="relative max-w-[90vw] max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <img src={img?.lg} alt={img?.alt || ''} className="max-w-full max-h-[85vh] object-contain" />
        {idx > 0 && <button onClick={() => setIdx(idx - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white bg-black/30 rounded-full p-2"><ChevronLeft size={24} /></button>}
        {idx < images.length - 1 && <button onClick={() => setIdx(idx + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white bg-black/30 rounded-full p-2"><ChevronRight size={24} /></button>}
      </div>
      <div className="absolute bottom-4 text-white/50 text-xs">{idx + 1} / {images.length}</div>
    </div>
  );
}

export default function ProductGallery({ images, selectedColor }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const stripRef = useRef(null);

  // Filter by color
  const filtered = (images || []).filter(img => {
    if (!selectedColor || !img.color_tag) return true;
    return img.color_tag === selectedColor || !img.color_tag;
  });

  // Reset index when color changes
  useEffect(() => { setActiveIdx(0); }, [selectedColor]);

  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  const goTo = useCallback((idx) => {
    setActiveIdx(Math.max(0, Math.min(idx, filtered.length - 1)));
  }, [filtered.length]);

  if (!filtered.length) return null;

  const current = filtered[activeIdx] || filtered[0];

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      goTo(diff > 0 ? activeIdx + 1 : activeIdx - 1);
    }
    setTouchStart(null);
  };

  // 1 image: large single
  if (filtered.length === 1) {
    return (
      <div className="relative cursor-zoom-in" onClick={() => setLightboxIdx(0)}>
        <img
          src={current.lg || current.md}
          alt={current.alt || ''}
          className="w-full object-contain"
          style={{ aspectRatio: current.width && current.height ? `${current.width}/${current.height}` : 'auto', maxHeight: '70vh', backgroundColor: 'var(--card-bg, var(--bg-color))' }}
          loading="eager"
        />
        <div className="absolute bottom-3 right-3 bg-black/30 rounded-full p-1.5"><ZoomIn size={14} className="text-white/70" /></div>
        {lightboxIdx !== null && <Lightbox images={filtered} startIndex={0} onClose={() => setLightboxIdx(null)} />}
      </div>
    );
  }

  return (
    <div>
      {/* Main image */}
      <div
        className="relative overflow-hidden cursor-zoom-in"
        onClick={() => setLightboxIdx(activeIdx)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{ backgroundColor: 'var(--card-bg, var(--bg-color))' }}
      >
        <img
          src={current.lg || current.md}
          alt={current.alt || ''}
          className="w-full object-contain"
          style={{
            aspectRatio: current.width && current.height ? `${current.width}/${current.height}` : '3/4',
            maxHeight: '60vh',
            transition: prefersReducedMotion ? 'none' : 'opacity 0.3s ease',
          }}
          loading="eager"
        />
        {/* Arrows */}
        {activeIdx > 0 && (
          <button onClick={(e) => { e.stopPropagation(); goTo(activeIdx - 1); }} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 rounded-full p-1.5 transition-colors"><ChevronLeft size={18} className="text-white" /></button>
        )}
        {activeIdx < filtered.length - 1 && (
          <button onClick={(e) => { e.stopPropagation(); goTo(activeIdx + 1); }} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/20 hover:bg-black/40 rounded-full p-1.5 transition-colors"><ChevronRight size={18} className="text-white" /></button>
        )}
        <div className="absolute bottom-3 right-3 bg-black/30 rounded-full p-1.5"><ZoomIn size={14} className="text-white/70" /></div>
      </div>

      {/* Thumbnail strip */}
      <div ref={stripRef} className="flex gap-2 mt-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
        {filtered.map((img, i) => (
          <button
            key={img.id || i}
            onClick={() => setActiveIdx(i)}
            className="flex-shrink-0 overflow-hidden transition-all"
            style={{
              width: '56px', height: '56px',
              border: i === activeIdx ? '2px solid var(--accent-color)' : '1px solid var(--border-color)',
              opacity: i === activeIdx ? 1 : 0.6,
            }}
          >
            <img src={img.sm || img.md} alt="" className="w-full h-full object-cover" loading="lazy" />
          </button>
        ))}
      </div>

      {lightboxIdx !== null && <Lightbox images={filtered} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />}
    </div>
  );
}
